import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  School, Users, GraduationCap, Activity,
  TrendingUp, Shield, ArrowRight, RefreshCw,
} from "lucide-react";
import { AuthStore } from "@/hooks/stores/AuthStore";
import SuperAdminLayout from "@/components/ui/superadmin/SuperAdminLayout";
import { superAdminApi, type PlatformStats, type EngagementMetrics } from "@/hooks/api/superAdminApi";

function StatCard({
  label, value, sub, icon: Icon, color,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex gap-4 items-start">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-slate-400 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-slate-600 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const map: Record<string, string> = {
    basic: "bg-slate-700 text-slate-300",
    standard: "bg-blue-500/20 text-blue-400",
    premium: "bg-violet-500/20 text-violet-400",
    enterprise: "bg-amber-500/20 text-amber-400",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${map[tier] ?? map.basic}`}>
      {tier}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isLoggedIn, userType, _hasHydrated } = AuthStore();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [engagement, setEngagement] = useState<EngagementMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!_hasHydrated) return;
    if (!isLoggedIn || userType !== "super_admin") {
      navigate("/admin/login");
    }
  }, [_hasHydrated, isLoggedIn, userType, navigate]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, e] = await Promise.all([
        superAdminApi.getStats(),
        superAdminApi.getEngagement(30),
      ]);
      setStats(s);
      setEngagement(e);
    } catch {
      setError("Failed to load stats. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const quickLinks = [
    { label: "Manage Schools", path: "/admin/schools", icon: School, desc: "View, create and edit schools" },
    { label: "Manage Users",   path: "/admin/users",   icon: Users, desc: "Students, admins and parents" },
    { label: "Audit Log",      path: "/admin/audit",   icon: Shield, desc: "All admin actions logged" },
  ];

  return (
    <SuperAdminLayout>
      <div className="space-y-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Overview</h1>
            <p className="text-slate-400 text-sm mt-0.5">Real-time stats across all schools and users</p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-sm text-slate-300 hover:text-white transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Stats grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 h-24 animate-pulse" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Total Users" value={stats.totalUsers.toLocaleString()} icon={Users} color="bg-teal-500/10 text-teal-400" />
            <StatCard label="Total Schools" value={stats.totalSchools.toLocaleString()} icon={School} color="bg-blue-500/10 text-blue-400" />
            <StatCard label="Total Students" value={stats.totalStudents.toLocaleString()} icon={GraduationCap} color="bg-violet-500/10 text-violet-400" />
            <StatCard label="Active Schools" value={stats.activeSchools.toLocaleString()} sub={`of ${stats.totalSchools}`} icon={Activity} color="bg-emerald-500/10 text-emerald-400" />
            <StatCard label="New This Month" value={stats.newUsersThisMonth.toLocaleString()} icon={TrendingUp} color="bg-amber-500/10 text-amber-400" />
            <StatCard label="Active Students" value={stats.activeStudents.toLocaleString()} sub={`of ${stats.totalStudents}`} icon={GraduationCap} color="bg-rose-500/10 text-rose-400" />
          </div>
        ) : null}

        {/* Two-col: breakdown + 30-day engagement */}
        {stats && engagement && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Users by type */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Users by type</h2>
              <div className="space-y-3">
                {Object.entries(stats.usersByType).map(([type, count]) => {
                  const pct = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                  const color: Record<string, string> = {
                    student: "bg-violet-500",
                    school_admin: "bg-teal-500",
                    super_admin: "bg-amber-500",
                    parent: "bg-blue-500",
                  };
                  return (
                    <div key={type} className="space-y-1">
                      <div className="flex justify-between text-xs text-slate-400">
                        <span className="capitalize">{type.replace("_", " ")}</span>
                        <span>{count.toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${color[type] ?? "bg-slate-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Schools by tier + 30-day engagement */}
            <div className="space-y-4">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Schools by tier</h2>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.schoolsByTier).map(([tier, count]) => (
                    <div key={tier} className="flex items-center gap-2 bg-slate-800 rounded-xl px-3 py-2">
                      <TierBadge tier={tier} />
                      <span className="text-sm font-bold text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3">
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Last 30 days</h2>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "New users",        value: engagement.newUsers },
                    { label: "New students",      value: engagement.newStudents },
                    { label: "Assessments",       value: engagement.newAssessments },
                    { label: "Achievements",      value: engagement.achievementsUnlocked },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-slate-800 rounded-xl p-3">
                      <p className="text-xl font-bold text-white">{value.toLocaleString()}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick links */}
        <div className="grid sm:grid-cols-3 gap-4">
          {quickLinks.map(({ label, path, icon: Icon, desc }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="group flex items-start gap-4 p-5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 rounded-2xl transition-all text-left"
            >
              <div className="w-9 h-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0 group-hover:bg-teal-500/20 transition-all">
                <Icon className="w-4.5 h-4.5 text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-teal-400 transition-colors mt-0.5 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
